import React, { useMemo } from 'react'
import {
    Box,
    Container,
    Heading,
    Text,
    Stack,
    Button,
    SimpleGrid,
    useColorModeValue,

} from '@chakra-ui/react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FiCpu, FiZap, FiCalendar, FiBarChart2 } from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'
import { useTranslation } from 'react-i18next'
import FeatureCard from './FeatureCard'


const MotionHeading = motion(Heading)
const MotionText = motion(Text)

// Hero section extracted
function HeroSection() {
    const { user } = useAuth()
    const { t } = useTranslation()
    const heroBg = useColorModeValue(
        'linear(to-r, teal.400, blue.500)',
        'linear(to-r, teal.700, blue.900)'
    )
    const textColor = useColorModeValue('gray.50', 'gray.100')

    return (
        <Box
            as="section"
            position="relative"
            bgGradient={heroBg}
            color="white"
            overflow="hidden"
            py={{ base: 24, md: 40 }}
        >
            <Box as="svg" viewBox="0 0 1440 320" position="absolute" bottom={0} left={0} w="full" h="80px" fill="white" opacity={0.7}>
                <path d="M0,224L48,202.7C96,181,192,139,288,112C384,85,480,75,576,101.3C672,128,768,192,864,197.3C960,203,1056,149,1152,117.3C1248,85,1344,75,1392,69.3L1440,64L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z" />
            </Box>

            <Container maxW="container.md" textAlign="center" position="relative" zIndex={1}>
                <MotionHeading
                    as="h1"
                    fontSize={{ base: '4xl', md: '6xl' }}
                    fontWeight="extrabold"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    bgClip="text"
                    bgGradient="linear(to-r, whiteAlpha.900, whiteAlpha.700)"
                >
                    {t('home.heroTitle')}
                </MotionHeading>

                <MotionText
                    fontSize={{ base: 'md', md: 'lg' }}
                    mt={4}
                    mb={8}
                    color={textColor}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3, duration: 0.6 }}
                >
                    {t('home.heroSubtitle')}
                </MotionText>

                <Stack direction={{ base: 'column', md: 'row' }} spacing={4} justify="center">
                    {user ? (
                        <Button
                            as={Link}
                            to="/meal-journal"
                            size="lg"
                            bg="white"
                            color="blue.600"
                            fontWeight="bold"
                            _hover={{ bg: 'whiteAlpha.800' }}
                            whileHover={{ scale: 1.03 }}
                            transition="all 0.2s"
                        >
                            {t('home.buttons.yourJournal')}
                        </Button>
                    ) : (
                        <Button
                            as={Link}
                            to="/register"
                            size="lg"
                            bg="white"
                            color="blue.600"
                            fontWeight="bold"
                            _hover={{ bg: 'whiteAlpha.800' }}
                            whileHover={{ scale: 1.03 }}
                            transition="all 0.2s"
                        >
                            {t('home.buttons.getStarted')}
                        </Button>
                    )}
                    <Button
                        as={Link}
                        to="/features"
                        size="lg"
                        variant="outline"
                        borderColor="whiteAlpha.800"
                        color="white"
                        _hover={{ bg: 'whiteAlpha.200' }}
                    >
                        {t('home.buttons.learnMore')}
                    </Button>
                </Stack>
            </Container>
        </Box>
    )
}

// Features section
function FeaturesSection() {
    const { t } = useTranslation()
    const features = useMemo(
        () => [
            { icon: FiCpu, key: 'aiAnalysis' },
            { icon: FiZap, key: 'smartRecommendations' },
            { icon: FiCalendar, key: 'holisticTracking' },
            { icon: FiBarChart2, key: 'dataDrivenInsights' },
        ], []
    )

    return (
        <Container as="section" maxW="container.xl" py={{ base: 16, md: 24 }}>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={10}>
                {features.map(({ icon, key }, idx) => (
                    <FeatureCard
                        key={key}
                        icon={icon}
                        title={t(`home.features.${key}.title`)}
                        desc={t(`home.features.${key}.desc`)}
                    />
                ))}
            </SimpleGrid>
        </Container>
    )
}

// Main page export
export default function HomePage() {
    return (
        <Box as="main">
            <HeroSection />
            <FeaturesSection />
        </Box>
    )
}
